import { Add, North, Remove, South } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { Accordion, Table, useAccordionButton } from 'react-bootstrap';
import DetailsPageLink from '../DetailsPageLink';

const CustomRowToggle = ({ children, eventKey, activeKey }) => {
  const decoratedOnClick = useAccordionButton(eventKey);
  const isRowActive = eventKey === activeKey;

  const activeClass = isRowActive ? 'table-secondary fw-semibold' : 'bg-white';

  return (
    <tr
      onClick={decoratedOnClick}
      className={`accordion-toggle-row align-middle border-0 ${activeClass}`}
      style={{ cursor: 'pointer' }}>
      {children}
    </tr>
  );
};

const AccordionTable = ({
  data,
  headerTabs,
  sortKey,
  sortOrder,
  handleSort,
  nestedTables,
  expandDefault = false,
}) => {
  const initialActiveKey =
    expandDefault && data?.length === 1
      ? data[0].project_id || 'row-0'
      : null;

  const [activeKey, setActiveKey] = useState(initialActiveKey);

  if (!data || data.length === 0) {
    return (
      <p className="text-muted p-3 border rounded">
        No data available for display.
      </p>
    );
  }

  const mainTableHeaders = headerTabs;
  const totalColumns = mainTableHeaders.length + 1;


  const handleAccordionToggle = (key) => {
    setActiveKey(key === activeKey ? null : key);
  };

  const renderSortIcon = (header) => {
    if (header.sortable && sortKey === header.key) {
      return sortOrder === 'asc' ? (
        <North fontSize="12px" />
      ) : (
        <South fontSize="12px" />
      );
    }
    return null;
  };

  const renderColGroup = () => (
    <colgroup>
      <col style={{ width: '5%' }} />

      {mainTableHeaders.map((header) => (
        <col
          key={header.key}
          style={{ width: header.width || 'auto' }}
        />
      ))}
    </colgroup>
  );

  return (
    <Accordion
      activeKey={activeKey}
      onSelect={handleAccordionToggle}
      className="border shadow-sm rounded-3 overflow-hidden">
      <Table responsive className="accordion-table mb-0 border-0" style={{ width: '100%', tableLayout: 'fixed' }} >
        {renderColGroup()}
        <thead>
          <tr>
            <th style={{ width: '5%' }} className="text-center"></th>
            {mainTableHeaders.map((header) => (
              <th
                key={header.key}
                style={{
                  width: header.width,
                  cursor: header.sortable ? 'pointer' : 'default',
                  fontSize: '14px',
                }}
                onClick={() => header.sortable && handleSort(header.key)}
                className={`text-center fw-semibold py-2 ${header.sortable ? 'cursor-pointer' : ''
                  }`}
              >
                <div className="d-flex align-items-center justify-content-center">
                  <span className="me-2">{header.label}</span>
                  {renderSortIcon(header)}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.map((row, index) => {
            const rowKey = row.project_id || `row-${index}`;
            const isRowActive = rowKey === activeKey;

            return (
              <React.Fragment key={rowKey}>
                <CustomRowToggle eventKey={rowKey} activeKey={activeKey}>
                  <td className="text-primary text-center border-0 py-2">
                    <Tooltip
                      title={isRowActive ? 'Click to collapse' : 'Click to expand'}
                      arrow
                    >
                      <IconButton size="small" disableRipple>
                        {isRowActive ? (
                          <Remove fontSize="small" />
                        ) : (
                          <Add fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </td>

                  {mainTableHeaders.map((header) => (
                    <td
                      key={header.key}
                      className={`text-center border-0 py-2 ${header.key === 'project_key' ? 'fw-bold' : ''
                        }`}
                    >
                      {header.formatContent
                        ? header.formatContent(row)
                        : row[header.key]}
                    </td>
                  ))}
                </CustomRowToggle>

                <tr>
                  <td colSpan={totalColumns} className="p-0 border-0">
                    <Accordion.Collapse eventKey={rowKey}>
                      <div className="p-4 border-bottom">
                        {nestedTables?.length > 0 && nestedTables.map((tableConfig, idx) => {
                          const tableData = row[tableConfig.dataKey] || [];
                          if (!tableData.length) return null;
                          const nestedColumns = mainTableHeaders.filter(
                            (header) => header.key !== 'project_key' && header.key !== 'project_name'
                          );

                          return (
                            <div key={idx} className="mb-4">
                              <h6 className="fw-semibold text-secondary mb-2">
                                {tableConfig.title}
                              </h6>

                              <Table
                                size="sm"
                                className="mb-0 border border-2"
                                responsive
                              >
                                {renderColGroup()}

                                <thead className="bg-light text-secondary">
                                  <tr>
                                    <th style={{ width: '5%' }}></th>
                                    <th className="text-center p-2 border-bottom" style={{ fontSize: '14px' }}>
                                      S.No.
                                    </th>
                                    <th className="text-center p-2 border-bottom" style={{ fontSize: '14px' }}>
                                      {tableConfig.MainColName}
                                    </th>
                                    {nestedColumns.map((header) => (
                                      <th
                                        key={header.key}
                                        className="text-center p-2 border-bottom"
                                        style={{ fontSize: '14px' }}
                                      >
                                        {header.label}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>


                                <tbody>
                                  {tableData.map((cRow, cIndex) => (
                                    <tr key={cIndex}>
                                      <td></td>
                                      <td className="text-center">
                                        {cIndex + 1}
                                      </td>
                                      <td className="text-center">
                                        <i className={tableConfig.iconClass} />
                                        <span>{cRow[tableConfig.labelKey]}</span>
                                      </td>
                                      {nestedColumns.map((header) => (
                                        <td
                                          key={header.key}
                                          className="text-center"
                                        >
                                          {header.key === 'percentage_done'
                                            ? `${cRow.percentage_done}%`
                                            : cRow[header.key] ?? 0}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          );
                        })}


                        <div className="d-flex justify-content-end pb-0">
                          <DetailsPageLink
                            to={`/project/${row.project_id}/testcases`}
                            label={`View All Test Cases for ${row.project_key}`}
                          />
                        </div>
                      </div>
                    </Accordion.Collapse>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </Accordion>
  );
};

export default AccordionTable;
